import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'

import { LaskuSharedService } from './lasku-shared.service'

import { AsiakkaanLahetettyLasku, Lasku, LaskuBase, LaskunLahetystapa, LaskunLahetystyyppi, LaskunTila, LaskunumeroTyyppi, LaskutettavatPalvelut } from '../../model/lasku'

export class LaskuLahetystietojenLuoja {
  constructor(
    private dateService: DateService,
    private currencyService: CurrencyService,
    private shared: LaskuSharedService,
    private timestampProvider: TimestampProviderBase,
  ) {}

  luoLahetystiedot(lasku: Lasku, asiakasId: string): AsiakkaanLahetettyLasku[] {
    const lahetystiedot: AsiakkaanLahetettyLasku[] = []

    const lahetystapa = this.annaLahetystapa(lasku)
    if (!lahetystapa) {
      return lahetystiedot
    }

    const mitatoity = lasku.tila === LaskunTila.mitatoity
    const juurilaskuNro = this.shared.annaMuotoiltuLaskunumero(lasku, lasku)
    const summat = this.shared.annaLaskunSummat(lasku)

    const paalaskunTiedot: AsiakkaanLahetettyLasku = {
      lahetetty: this.timestampProvider.now(),
      juurilaskuAvain: lasku.avain,
      juurilaskuNro: juurilaskuNro,
      kanava: lahetystapa,
      kasiteltavaAvain: lasku.avain,
      kasiteltavanrotyyppi: lasku.nrotyyppi,
      kasiteltavaNro: juurilaskuNro,
      verollinensumma: mitatoity ? 0 : this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaKaikki),
      verotonsumma: mitatoity ? 0 : this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaIlmanAlv),
      laskunAsiakkaanAvain: lasku.asiakas.avain,
      hakupvm: Number(this.dateService.muotoileTallennusPaivamaara(lasku.pvml)),
      paiva: lasku.pvml.day,
      kuukausi: lasku.pvml.month,
      vuosi: lasku.pvml.year,
      laskunLahetystyyppi: lasku.lahetystyyppi,
    }

    this.luoLaskunLaskutustiedotTarvittaessa(lasku, lasku, paalaskunTiedot, asiakasId)
    lahetystiedot.push(paalaskunTiedot)

    const liittyvatLaskut = this.shared.annaLiittyvatLaskut(lasku)
    if (!liittyvatLaskut.length) {
      return lahetystiedot
    }

    let edellinenTavallinen: LaskuBase = lasku
    for (const korvaava of liittyvatLaskut) {
      const korvaavanlahetystapa = this.annaLahetystapa(korvaava)
      if (!korvaavanlahetystapa) {
        continue
      }

      const korvaavanSummat = this.shared.annaLaskunSummat(korvaava)

      let verollinen = korvaavanSummat.yhteensaKaikki
      let veroton = korvaavanSummat.yhteensaIlmanAlv
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS || korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN) {
        const edellisenSummat = this.shared.annaLaskunSummat(edellinenTavallinen)
        verollinen = verollinen.minus(edellisenSummat.yhteensaKaikki)
        veroton = veroton.minus(edellisenSummat.yhteensaIlmanAlv)
        edellinenTavallinen = korvaava
      } else if (korvaava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        const kulutJaKorot = this.shared.annaMuistutuslaskunKulutTalleLaskulle(lasku, korvaava)
        verollinen = kulutJaKorot.korot.plus(kulutJaKorot.kulut)
        veroton = verollinen
      }

      const korvaavanTiedot: AsiakkaanLahetettyLasku = {
        lahetetty: this.timestampProvider.now(),
        juurilaskuAvain: lasku.avain,
        juurilaskuNro: juurilaskuNro,
        kanava: korvaavanlahetystapa,
        kasiteltavaAvain: korvaava.avain,
        kasiteltavanrotyyppi: korvaava.nrotyyppi,
        kasiteltavaNro: this.shared.annaMuotoiltuLaskunumero(lasku, korvaava),
        verollinensumma: mitatoity ? 0 : this.currencyService.muutaBigDecimalRahaksi(verollinen),
        verotonsumma: mitatoity ? 0 : this.currencyService.muutaBigDecimalRahaksi(veroton),
        laskunAsiakkaanAvain: korvaava.asiakas.avain,
        hakupvm: Number(this.dateService.muotoileTallennusPaivamaara(korvaava.pvml)),
        paiva: korvaava.pvml.day,
        kuukausi: korvaava.pvml.month,
        vuosi: korvaava.pvml.year,
        laskunLahetystyyppi: lasku.lahetystyyppi,
      }

      this.luoLaskunLaskutustiedotTarvittaessa(lasku, korvaava, korvaavanTiedot, asiakasId)
      lahetystiedot.push(korvaavanTiedot)
    }
    return lahetystiedot
  }

  private luoLaskunLaskutustiedotTarvittaessa(juurilasku: Lasku, kasiteltava: LaskuBase, laskunTiedot: AsiakkaanLahetettyLasku, asiakasId: string) {
    if (asiakasId === '9') {
      laskunTiedot.kertalaskuAlviton = 0
      laskunTiedot.kertalaskuAlvillinen = 0
      laskunTiedot.jatkuvatLaskutAlviton = 0
      laskunTiedot.jatkuvatLaskutAlvillinen = 0
      laskunTiedot.laskujenVastaanottoAlviton = 0
      laskunTiedot.laskujenVastaanottoAlvillinen = 0
      laskunTiedot.maksupalveluAlviton = 0
      laskunTiedot.maksupalveluAlvillinen = 0

      // Muistutuksille on oma käsittely
      if (juurilasku.avain !== kasiteltava.avain && kasiteltava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        laskunTiedot.huomautuskulu = 0
        laskunTiedot.korkokulu = 0

        const muistutuslaskunKulut = this.shared.annaMuistutuslaskunKulutTalleLaskulle(juurilasku, kasiteltava)
        if (muistutuslaskunKulut.kulut.gt('0')) {
          laskunTiedot.huomautuskulu = this.currencyService.muutaBigDecimalRahaksi(muistutuslaskunKulut.kulut)
        }
        if (muistutuslaskunKulut.korot.gt('0')) {
          laskunTiedot.korkokulu = this.currencyService.muutaBigDecimalRahaksi(muistutuslaskunKulut.korot)
        }
      } else {
        for (const tuote of kasiteltava.tuotteet ?? []) {
          const hinta = this.shared.annaRivinSummatLaskettunaKappalehinnasta(tuote)
          const alviton = this.currencyService.muutaBigDecimalRahaksi(hinta.yhteensaAlvnKanssa.minus(hinta.alvta))
          const alvillinen = this.currencyService.muutaBigDecimalRahaksi(hinta.yhteensaAlvnKanssa)

          if (tuote.tuote.avain === LaskutettavatPalvelut.JATKUVA_KUUKAUSILASKUTUS) {
            laskunTiedot.jatkuvatLaskutAlviton += alviton
            laskunTiedot.jatkuvatLaskutAlvillinen += alvillinen
          } else if (tuote.tuote.avain === LaskutettavatPalvelut.SAHKOISTEN_LASKUJEN_VASTAANOTTO) {
            laskunTiedot.laskujenVastaanottoAlviton += alviton
            laskunTiedot.laskujenVastaanottoAlvillinen += alvillinen
          } else if (tuote.tuote.avain === LaskutettavatPalvelut.MAKSUT) {
            laskunTiedot.maksupalveluAlviton += alviton
            laskunTiedot.maksupalveluAlvillinen += alvillinen
          } else if (juurilasku.lahetystyyppi === LaskunLahetystyyppi.KERTA) {
            laskunTiedot.kertalaskuAlviton += alviton
            laskunTiedot.kertalaskuAlvillinen += alvillinen
          }
        }
      }
    }
  }

  private annaLahetystapa(lasku: LaskuBase): LaskunLahetystapa {
    if (lasku.email && lasku.email.start) {
      return LaskunLahetystapa.SAHKOPOSTI
    } else if (lasku.print && lasku.print.start) {
      return LaskunLahetystapa.ITSE
    } else if (lasku.sahkoinen && lasku.sahkoinen.start) {
      return LaskunLahetystapa.SAHKOINEN
    } else if (this.shared.onkoUlkoinenLasku(lasku)) {
      return LaskunLahetystapa.MUUALLA
    }
    return null
  }
}
